/**
 * E-Rechnungspflicht für Ausgangsrechnungen (§ 14 UStG, Wachstumschancengesetz).
 *
 * Ab dem in den Einstellungen gewählten Datum (01.01.2027 bei > 800.000 € Vorjahresumsatz,
 * sonst 01.01.2028) dürfen Rechnungen an inländische Unternehmen nur noch als E-Rechnung
 * ausgestellt werden. Ausgenommen: Privatkunden, Kleinbetragsrechnungen bis 250 € brutto,
 * Empfänger im Ausland.
 */

export const SMALL_INVOICE_LIMIT_EUR = 250;

const EINVOICE_DOCUMENT_LABELS = new Set(['Rechnung', 'Abschlagsrechnung', 'Schlussrechnung', 'Stornorechnung', 'Gutschrift']);

export type EinvoiceMandateInput = {
  /** company_settings.einvoice_mandatory_from (YYYY-MM-DD) oder null = keine Pflicht hinterlegt. */
  mandatoryFrom: string | null | undefined;
  /** Rechnungsdatum YYYY-MM-DD. */
  issueDate: string | null | undefined;
  documentTypeLabel: string;
  isBusinessCustomer: boolean;
  grossAmount: number;
  /** USt-IdNr. des Empfängers, falls vorhanden (Länderpräfix ≠ DE = Ausland). */
  recipientVatId?: string | null;
};

export function isEinvoiceMandatory(input: EinvoiceMandateInput): boolean {
  const from = String(input.mandatoryFrom ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) return false;
  const issue = String(input.issueDate ?? '').slice(0, 10);
  const effectiveIssue = /^\d{4}-\d{2}-\d{2}$/.test(issue) ? issue : new Date().toISOString().slice(0, 10);
  if (effectiveIssue < from) return false;
  if (!EINVOICE_DOCUMENT_LABELS.has(input.documentTypeLabel.trim())) return false;
  if (!input.isBusinessCustomer) return false;
  if (Math.abs(Number(input.grossAmount) || 0) <= SMALL_INVOICE_LIMIT_EUR) return false;
  const vatPrefix = String(input.recipientVatId ?? '').trim().toUpperCase().slice(0, 2);
  if (/^[A-Z]{2}$/.test(vatPrefix) && vatPrefix !== 'DE') return false;
  return true;
}

export const EINVOICE_MANDATORY_BLOCK_MESSAGE =
  'E-Rechnungspflicht: Diese Rechnung an ein Unternehmen muss als E-Rechnung ausgestellt werden. ' +
  'Die E-Rechnungs-Daten konnten nicht erzeugt werden – bitte die genannten Angaben ergänzen. ' +
  'Ein reines PDF wird nicht ausgestellt.';

/** Vorschläge für die Einstellung: Stichtag je nach Vorjahresumsatz. */
export const EINVOICE_MANDATE_OPTIONS = [
  { value: '', label: 'Keine Pflicht hinterlegt' },
  { value: '2027-01-01', label: 'Ab 01.01.2027 (Vorjahresumsatz über 800.000 €)' },
  { value: '2028-01-01', label: 'Ab 01.01.2028 (alle übrigen Unternehmen)' },
] as const;
